import { Clock, Lightbulb, ListChecks, MessageCircle, Package, Wrench, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import {
  assemblySteps,
  assemblyTips,
  assemblyVideo,
  contact,
  parts,
  screws,
  tools,
} from "@kanata/shared";
import { Seo, SITE_URL } from "@/components/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { SectionHeading } from "@/components/sections/SectionHeading";
import { Button } from "@/components/ui/button";
import { YouTubeFacade } from "@/components/ui/YouTubeFacade";
import {
  assemblyHero,
  assemblyImages,
  assemblyVideoPoster,
  explodedDiagram,
  partsPhoto,
} from "@/lib/images";

const TITLE = "Cara rakit standing desk Kanata — panduan langkah demi langkah";
const DESCRIPTION =
  "Panduan merakit standing desk Kanata: daftar isi dus, alat yang dipakai, dan 15 langkah berfoto. Sekitar 20 menit, tanpa keahlian khusus.";

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: TITLE,
  description: DESCRIPTION,
  totalTime: `PT${assemblyVideo.estimatedMinutes}M`,
  tool: tools.map((t) => ({ "@type": "HowToTool", name: t })),
  supply: parts.map((p) => ({ "@type": "HowToSupply", name: p.name })),
  step: assemblySteps.map((s) => ({
    "@type": "HowToStep",
    position: s.n,
    name: s.title,
    text: s.description,
    image: SITE_URL + assemblyImages[s.imageKey],
  })),
};

const videoSchema = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: "Standing desk Kanata — tutorial perakitan",
  description: DESCRIPTION,
  thumbnailUrl: SITE_URL + assemblyVideoPoster,
  uploadDate: "2024-01-01",
  contentUrl: assemblyVideo.url,
  embedUrl: `https://www.youtube.com/embed/${assemblyVideo.id}`,
};

const metaChips = [
  { icon: Clock, label: `± ${assemblyVideo.estimatedMinutes} menit` },
  { icon: ListChecks, label: `${assemblySteps.length} langkah` },
  { icon: Wrench, label: "Tanpa keahlian khusus" },
];

/**
 * The assembly guide.
 *
 * Load-bearing for the pasang opt-out: a customer who unchecks installation at
 * checkout is sent here, so this page has to be good enough that declining
 * installation is a real choice rather than a trap.
 */
export default function Assembly() {
  return (
    <>
      <Seo
        title={TITLE}
        description={DESCRIPTION}
        path="/perakitan"
        image={assemblyHero}
        jsonLd={[howToSchema, videoSchema]}
      />

      <PageHero
        eyebrow="Panduan perakitan"
        title="Rakit sendiri? Ini caranya."
        lead="Lima belas langkah, masing-masing ada fotonya. Sekitar 20 menit kalau ada yang bantu membalik mejanya."
        image={assemblyHero}
      >
        <div className="mt-6 flex flex-wrap gap-2">
          {metaChips.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm"
            >
              <c.icon className="size-3.5" />
              {c.label}
            </span>
          ))}
        </div>
      </PageHero>

      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <div className="rounded-2xl border border-ink/8 bg-brand-tint p-6 sm:p-7">
            <p className="leading-relaxed text-ink/75">
              <strong className="font-medium text-ink">Kamu tidak harus rakit sendiri.</strong>{" "}
              Tim kami bisa datang, rakit, dan pastikan mejanya jalan — untuk Jakarta, Tangerang, Depok, dan Bekasi, dengan biaya sesuai zona.
              Halaman ini untuk kamu yang memang lebih suka mengerjakan sendiri.
            </p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/katalog">Lihat semua meja</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="pb-14 sm:pb-20">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <SectionHeading eyebrow="Video" title="Lihat dulu keseluruhannya." />
          <div className="mt-6 overflow-hidden rounded-2xl">
            <YouTubeFacade
              id={assemblyVideo.id}
              poster={assemblyVideoPoster}
              title="Tutorial perakitan standing desk Kanata"
            />
          </div>
          <a
            href={assemblyVideo.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-ink/60 transition-colors hover:text-brand"
          >
            <Youtube className="size-4" />
            Buka di YouTube ({assemblyVideo.videoMinutes} menit)
          </a>
        </div>
      </section>

      <section className="border-t border-ink/8 bg-cream/50 py-14 sm:py-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <SectionHeading eyebrow="Sebelum mulai" title="Cek isi dusnya dulu." />

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="overflow-hidden rounded-2xl bg-white">
              <img src={partsPhoto} alt="Isi dus standing desk Kanata" className="w-full object-cover" />
            </div>
            <div className="overflow-hidden rounded-2xl bg-white p-4">
              <img src={explodedDiagram} alt="Diagram bagian standing desk" className="w-full object-contain" />
            </div>
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Panel icon={Package} title="Komponen">
              <ul className="space-y-1.5 text-sm">
                {parts.map((p) => (
                  <li key={p.n} className="flex gap-2 text-ink/70">
                    <span className="w-5 shrink-0 tabular-nums text-ink/40">{p.n}.</span>
                    <span>
                      {p.name} <span className="text-ink/45">×{p.qty}</span>
                      {p.note && <span className="text-ink/45"> — {p.note}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel icon={Wrench} title="Baut &amp; alat bawaan">
              <ul className="space-y-1.5 text-sm">
                {screws.map((s) => (
                  <li key={s.code} className="flex gap-2 text-ink/70">
                    <span className="w-5 shrink-0 font-medium text-brand">{s.code}</span>
                    <span>
                      {s.spec} <span className="text-ink/45">×{s.qty}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel icon={ListChecks} title="Siapkan juga">
              <ul className="space-y-1.5 text-sm">
                {tools.map((t) => (
                  <li key={t} className="text-ink/70">
                    {t}
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <SectionHeading eyebrow="Langkah" title={`${assemblySteps.length} langkah sampai berdiri.`} />

          <ol className="mt-10 space-y-10">
            {assemblySteps.map((step) => (
              <li key={step.n} className="grid gap-5 sm:grid-cols-[1fr_1.1fr] sm:items-center sm:gap-8">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-ink text-sm font-medium text-white">
                      {step.n}
                    </span>
                    <h3 className="text-lg font-medium text-ink">{step.title}</h3>
                  </div>
                  <p className="mt-3 leading-relaxed text-ink/70">{step.description}</p>
                  {step.screw && (
                    <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-light px-3 py-1 text-xs font-medium text-brand-dark">
                      Pakai {step.screw}
                    </p>
                  )}
                </div>
                <div className="overflow-hidden rounded-2xl bg-cream">
                  <img
                    src={assemblyImages[step.imageKey]}
                    alt={`Langkah ${step.n}: ${step.title}`}
                    loading="lazy"
                    className="w-full object-cover"
                  />
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-t border-ink/8 bg-cream/50 py-14 sm:py-20">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <SectionHeading eyebrow="Tips" title="Yang bikin lebih lancar." />
          <ul className="mt-8 grid gap-4 sm:grid-cols-2">
            {assemblyTips.map((tip) => (
              <li key={tip} className="flex gap-3 rounded-2xl border border-ink/8 bg-white p-5">
                <Lightbulb className="mt-0.5 size-4.5 shrink-0 text-brand" />
                <p className="text-sm leading-relaxed text-ink/70">{tip}</p>
              </li>
            ))}
          </ul>

          <div className="mt-10 rounded-2xl border border-ink/8 bg-white p-6 sm:p-7">
            <h2 className="text-lg font-medium text-ink">Mentok di satu langkah?</h2>
            <p className="mt-2 leading-relaxed text-ink/65">
              Kirim foto posisi kamu sekarang lewat WhatsApp. Biasanya kelihatan langsung di mana
              salahnya.
            </p>
            <Button asChild className="mt-5">
              <a href={contact.whatsappUrl} target="_blank" rel="noopener noreferrer" data-wa-placement="assembly">
                <MessageCircle />
                Tanya lewat WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}

function Panel({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Package;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-ink/8 bg-white p-5">
      <div className="flex items-center gap-2">
        <Icon className="size-4 text-brand" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-ink/60">{title}</h3>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}
